import { z } from 'zod';

// ─── Group Identifiers ──────────────────────────────────────────────────────

export const JnJProdGroupId = 'c1817a7f-1bdc-4688-83ea-67fd0f456897';
export const TrinitySandboxGroupId = '87e4dcfa-1954-4fb0-8daa-0927c7be07c0';
export const TrinityProdGroupId = '26a39ca6-0c62-4c98-a9d6-35f8cc1129fd';

export type HackathonAgentGroupId =
  | typeof JnJProdGroupId
  | typeof TrinitySandboxGroupId
  | typeof TrinityProdGroupId;

// ─── Group-specific Terminology ─────────────────────────────────────────────

export type HackathonTerminology = {
  primaryBrand: string;
  primaryDisease: string;
  brandNames: Record<string, string>;
  segmentNames: Record<string, string>;
  clinicalTrials: Record<string, string>;
  archetypes: Record<string, string>;
};

const DEFAULT_TERMINOLOGY: HackathonTerminology = {
  primaryBrand: 'Elderberry',
  primaryDisease: 'Multiple Myeloma',
  brandNames: {
    Apple: 'Apple',
    Banana: 'Banana',
    Cherry: 'Cherry',
    Date: 'Date',
    Elderberry: 'Elderberry',
    Fig: 'Fig',
    Grape: 'Grape',
    Honeydew: 'Honeydew',
    Kiwi: 'Kiwi',
    Lemon: 'Lemon',
    Mango: 'Mango',
    Nectarine: 'Nectarine',
    Orange: 'Orange',
    Papaya: 'Papaya',
    Quince: 'Quince',
  },
  segmentNames: {
    'Elderberry-Dominant': 'Elderberry-Dominant',
    'Elderberry-Dabbler': 'Elderberry-Dabbler',
    'Doublet-Disciple': 'Doublet-Disciple',
    'Grape-Adopter': 'Grape-Adopter',
    'Defender-of-Banana-Fig-Lemon': 'Defender-of-Banana-Fig-Lemon',
  },
  clinicalTrials: {
    PERSEUS: 'PERSEUS',
    CEPHEUS: 'CEPHEUS',
    MAIA: 'MAIA',
  },
  archetypes: {
    Architect: 'Architect',
    Traditionalist: 'Traditionalist',
    Strategist: 'Strategist',
    Neophile: 'Neophile',
    Warrior: 'Warrior',
    Conservator: 'Conservator',
  },
};

const TRINITY_PROD_TERMINOLOGY: HackathonTerminology = {
  primaryBrand: 'Trionex',
  primaryDisease: 'Plasmacytoid Lymphoid Neoplasm',
  brandNames: {
    Apple: 'Slameva',
    Banana: 'Renvalo',
    Cherry: 'Anvitra',
    Date: 'Dexalon',
    Elderberry: 'Trionex',
    Fig: 'Vortexib',
    Grape: 'Pomatryn',
    Honeydew: 'Ixoryn',
    Kiwi: 'Selnoxa',
    Lemon: 'Lendrivex',
    Mango: 'Melfaron',
    Nectarine: 'Bispecta',
    Orange: 'Talquerin',
    Papaya: 'Cartivex',
    Quince: 'Carzomex',
  },
  segmentNames: {
    'Elderberry-Dominant': 'Trionex-Champion',
    'Elderberry-Dabbler': 'Trionex-Tester',
    'Doublet-Disciple': '2-Combo-Disciple',
    'Grape-Adopter': 'Pomatryn-Adopter',
    'Defender-of-Banana-Fig-Lemon': 'Defender-of-Renvalo-Vortexib-Lendrivex',
  },
  clinicalTrials: {
    PERSEUS: 'ORION',
    CEPHEUS: 'DRACO',
    MAIA: 'LYRA',
  },
  archetypes: {
    Architect: 'Terse Academic',
    Traditionalist: 'Storyteller',
    Strategist: 'Data Focused',
    Neophile: 'Pragmatic Community',
    Warrior: 'Cautious Methodical',
    Conservator: 'Confident Opinionated',
    'Efficient Terse': 'Efficient Terse',
    'Mentor Explainer': 'Mentor Explainer',
    'Patient Centric': 'Patient Centric',
    'Guideline Driven': 'Guideline Driven',
    'Innovator Early': 'Innovator Early',
    'Analytical Skeptic': 'Analytical Skeptic',
  },
};

/**
 * Resolves group-specific terminology (brand names, segments, trials, archetypes).
 * Trinity prod group gets alternate naming; all others get defaults.
 */
export function getHackathonTerminology(groupIdentifier: string): HackathonTerminology {
  if (groupIdentifier === TrinityProdGroupId) {
    return TRINITY_PROD_TERMINOLOGY;
  }
  return DEFAULT_TERMINOLOGY;
}

// ─── Getter Functions ───────────────────────────────────────────────────────

/** Returns the list of brand names for the given group. */
export function getHackathonBrandNames(groupIdentifier: string): string[] {
  return Object.values(getHackathonTerminology(groupIdentifier).brandNames);
}

/** Returns the list of segment names for the given group. */
export function getHackathonSegmentNames(groupIdentifier: string): string[] {
  return Object.values(getHackathonTerminology(groupIdentifier).segmentNames);
}

/** Returns the list of clinical trial names for the given group. */
export function getHackathonClinicalTrials(groupIdentifier: string): string[] {
  return Object.values(getHackathonTerminology(groupIdentifier).clinicalTrials);
}

/** Returns the list of archetype names for the given group. */
export function getHackathonArchetypes(groupIdentifier: string): string[] {
  return Object.values(getHackathonTerminology(groupIdentifier).archetypes);
}

// ─── Combined Zod Schemas (accept values from ALL groups) ───────────────────

function collectAllValues(key: keyof HackathonTerminology): string[] {
  const all = new Set<string>();
  for (const terminology of [DEFAULT_TERMINOLOGY, TRINITY_PROD_TERMINOLOGY]) {
    const mapping = terminology[key];
    if (typeof mapping === 'string') {
      all.add(mapping);
    } else {
      for (const v of Object.values(mapping)) {
        all.add(v);
      }
    }
  }
  return [...all];
}

/** Zod schema accepting brand names from any group. */
export const AllBrandNamesSchema = z.enum(collectAllValues('brandNames') as [string, ...string[]]);

/** Zod schema accepting segment names from any group. */
export const AllSegmentNamesSchema = z.enum(collectAllValues('segmentNames') as [string, ...string[]]);

/** Zod schema accepting clinical trial names from any group. */
export const AllClinicalTrialsSchema = z.enum(collectAllValues('clinicalTrials') as [string, ...string[]]);

/** Zod schema accepting archetype names from any group. */
export const AllArchetypesSchema = z.enum(collectAllValues('archetypes') as [string, ...string[]]);

/** All brand names across all groups. */
export const ALL_BRAND_NAMES = collectAllValues('brandNames');

/** All segment names across all groups. */
export const ALL_SEGMENT_NAMES = collectAllValues('segmentNames');

/** All clinical trial names across all groups. */
export const ALL_CLINICAL_TRIALS = collectAllValues('clinicalTrials');

/** All archetype names across all groups. */
export const ALL_ARCHETYPES = collectAllValues('archetypes');
